<script setup>
    /**
     * Vue Scripts
     */
    import {ref} from "vue";
    import {geocodeByAddress, getLatLng, usePlacesAutocomplete} from "vue-use-places-autocomplete";

    /**
     * Vue Components
     *
     * @link https://vue-multiselect.js.org/
     */
    import Multiselect from 'vue-multiselect'

    const props = defineProps({
        apiKey: String,
        id: String,
        label: String,
        placeholder: String,
        selectOnEnter: Boolean,
        value: String,
    })
    const emit = defineEmits(['getLatLng'])

    const query = ref('')
    const currentToken = ref(undefined)

    const {
        suggestions,
        loading,
        sessionToken,
        refreshSessionToken,
    } = usePlacesAutocomplete(
        query,
        {
            apiKey: props.apiKey,
            debounce: 500,
            minLengthAutocomplete: 3,
            withSessionToken: currentToken,
            apiOptions: {
                componentRestrictions: {
                    country: ["us", "pr"],
                },
                fields: ["name", "address_components", "geometry.location", "place_id", "formatted_address"],
            }
        }
    )

    currentToken.value = sessionToken;

    /**
     * Triggers updating the list of locations
     *
     * @param {Object|String} search The search value and response
     */
    const startSearch = (search) => search.length && (query.value = search)

    /**
     * When the user selects the choice, fetches the Lat/Lng
     * from Google based on the Address
     *
     * @param content
     * @returns {Promise<void>}
     */
    const selectedValue = async (content) => {
        const results = await geocodeByAddress(content.description);

        await getLatLng(results[0])
            .then(coords => emit('getLatLng', {name: content.description, coords}))
            .catch(err => console.error(err))
    }

    const limitText = (count) => `+${count}`

    const getValue = (value) => value.description

</script>

<template>
    <div class="input-item">
        <label
            :for="`${id}-ajax`"
            v-html="label"
        />

        <Multiselect
            v-model="query"

            :allow-empty="true"
            :clear-on-select="false"
            :close-on-select="true"
            :hide-selected="false"
            :id="`${id}-ajax`"
            :internal-search="false"
            :limit="3"
            :limit-text="limitText"
            :loading="loading"
            :max-height="600"
            :multiple="false"
            :options="suggestions"
            :options-limit="300"
            :preserveSearch="true"
            :placeholder="placeholder"
            :searchable="true"
            :show-labels="false"
            :show-no-options="false"
            :show-no-results="false"
            :value="query"

            label="description"
            name="description"
            open-direction="bottom"
            track-by="description"

            @search-change="startSearch"
            @select="selectedValue"
        >
            <template #singleLabel="{option}">
                <p
                    class="multiselect__tags-value"
                    v-html="getValue(option)"
                />
            </template>

            <!-- The results -->
            <template #option="{option}">
                <p
                    class="dropdown__results-item-text"
                    v-html="getValue(option)"
                />
            </template>

            <template #selection="{value, search, isOpen}">
                <div
                    v-if="query.length"
                    v-show="!isOpen"
                    class="multiselect__search"
                >
                    <p
                        class="multiselect__search-text"
                        v-html="query"
                    />
                </div>
            </template>

            <template #caret></template>

        </Multiselect>
    </div>
</template>

<script>
    import "vue-multiselect/dist/vue-multiselect.css"

    export default {
        name: "Input Autocomplete"
    }
</script>

<style lang="scss">
    .multiselect__select {
        display: none;
    }
</style>
